import { useCallback, useState, type RefObject } from "react";
import { message } from "antd";
import type { EditorRef } from "react-email-editor";
import { useTranslation } from "react-i18next";
import { resendCampaignEmail, sendCampaignEmail } from "src/services/CampaignAPI";
import { setCampaignStatus } from "src/services/ChatbotAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { formatNumber } from "src/utils/numberFormat";
import { getThrownApiDetail, showApiDetailToast } from "src/utils/showApiDetailToast";
import type { CampaignSenderDetails, GeneratedTemplate } from "../AiCampaignTypes";
import { exportEditorHtml } from "../EmailEditorUtils";

interface DeliveryStatus {
  type: "sent" | "scheduled";
  date?: string;
  time?: string;
}

export interface ScheduleSelection {
  sendAt: string;
  date: string;
  time: string;
}

interface UseCampaignDeliveryParams {
  editorRef: RefObject<EditorRef>;
  template: GeneratedTemplate | null;
  campaignName: string;
  subjectLine: string;
  senderDetails: CampaignSenderDetails;
  previousCampaignId?: string;
  replyTo?: string;
  draftId?: number | null;
  onDelivered?: () => void;
}

export const useCampaignDelivery = ({
  editorRef,
  template,
  campaignName,
  subjectLine,
  senderDetails,
  previousCampaignId,
  replyTo,
  draftId,
  onDelivered,
}: UseCampaignDeliveryParams) => {
  const { t, i18n } = useTranslation();
  const [isSending, setIsSending] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus | null>(null);

  const deliver = useCallback(
    async (schedule?: ScheduleSelection): Promise<boolean> => {
      setIsSending(true);
      try {
        const html = (await exportEditorHtml(editorRef.current?.editor)) || template?.html;
        if (!html) {
          throw new Error("Missing campaign HTML");
        }

        const basePayload = {
          campaign_name: campaignName.trim(),
          send_to: senderDetails.recipients,
          dont_send_to: senderDetails.exclusions,
          html,
          send_at: schedule?.sendAt ?? new Date().toISOString(),
          company_name_engagements: senderDetails.fromName,
        };

        if (previousCampaignId) {
          await resendCampaignEmail({ ...basePayload, previous_campaign_id: previousCampaignId });
        } else {
          const response = await sendCampaignEmail({
            ...basePayload,
            subject_line: subjectLine.trim() || undefined,
            reply_to: replyTo?.trim() || undefined,
            draft_id: draftId ?? undefined,
            lang: getApiLangFromI18n(i18n),
          });
          if (response?.status === "warning") {
            message.warning(response.message || t("campaignFlow.aiEmailEditor.sendError"));
            return false;
          }
          const skippedCount = response?.suppressed_unsubscribed;
          if (typeof skippedCount === "number" && skippedCount > 0) {
            message.info(
              t("campaignFlow.aiEmailEditor.skippedUnsubscribed", {
                count: skippedCount,
                formattedCount: formatNumber(skippedCount, {}, i18n.language),
              })
            );
          }
        }

        setCampaignStatus("completed");
        onDelivered?.();
        if (schedule) {
          message.success(t("campaignFlow.aiEmailEditor.campaignScheduled"));
          setDeliveryStatus({ type: "scheduled", date: schedule.date, time: schedule.time });
        } else {
          message.success(t("campaignFlow.aiEmailEditor.campaignSent"));
          setDeliveryStatus({ type: "sent" });
        }
        return true;
      } catch (error) {
        const detail = getThrownApiDetail(error);
        if (detail) {
          showApiDetailToast(detail);
        } else {
          message.error(t("campaignFlow.aiEmailEditor.sendError"));
        }
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [
      campaignName,
      draftId,
      editorRef,
      i18n,
      onDelivered,
      previousCampaignId,
      replyTo,
      senderDetails,
      subjectLine,
      t,
      template,
    ]
  );

  const sendNow = useCallback(() => {
    deliver();
  }, [deliver]);

  const schedule = useCallback(
    async (selection: ScheduleSelection) => {
      if (await deliver(selection)) {
        setIsScheduleOpen(false);
      }
    },
    [deliver]
  );

  const openSchedule = useCallback(() => setIsScheduleOpen(true), []);
  const closeSchedule = useCallback(() => setIsScheduleOpen(false), []);
  const clearDeliveryStatus = useCallback(() => setDeliveryStatus(null), []);

  return {
    isSending,
    isScheduleOpen,
    deliveryStatus,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
  };
};

